/**
 * @fileoverview Utility functions for date manipulation and formatting.
 * @module shared/utils/dateUtils
 */

import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns'
import { enUS, pl } from 'date-fns/locale'
import type { AggregationType } from '@/shared/context/FilterContext'

/** Matches an ISO week-based bucket key produced by the statistics API, e.g. "2026-W21". */
export const ISO_WEEK_PERIOD_PATTERN = /^\d{4}-W\d{2}$/

/**
 * Checks whether a statistics period key is a weekly (ISO week) bucket.
 *
 * @param period - Period string from the statistics API
 * @returns True when the period is an ISO week key like "2026-W21"
 * @example
 * ```typescript
 * isIsoWeekPeriod('2026-W21') // => true
 * isIsoWeekPeriod('2026-05-18') // => false
 * ```
 */
export function isIsoWeekPeriod(period: string): boolean {
  return ISO_WEEK_PERIOD_PATTERN.test(period)
}

/**
 * Formats an ISO week bucket key (e.g. "2026-W21") as the explicit Monday–Sunday
 * date range it represents, collapsing the shared month. Falls back to the raw
 * string if it cannot be parsed.
 *
 * @param period - ISO week key (e.g. "2026-W21")
 * @param locale - Current UI locale ('en' or 'pl')
 * @returns Human-readable week range
 * @example
 * ```typescript
 * formatIsoWeekRange('2026-W21') // => "18 – 24 May"
 * formatIsoWeekRange('2026-W18') // => "27 Apr – 3 May"
 * ```
 */
export function formatIsoWeekRange(period: string, locale: string = 'en'): string {
  try {
    const dateLocale = locale === 'pl' ? pl : enUS
    const start = parseISO(period)
    const end = addDays(start, 6)
    const sameMonth =
      start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
    const startPattern = sameMonth ? 'd' : 'd MMM'
    return `${format(start, startPattern, { locale: dateLocale })} – ${format(end, 'd MMM', { locale: dateLocale })}`
  } catch {
    return period
  }
}

/**
 * Gets the date range for the current month.
 *
 * @returns Object containing start and end dates for current month
 * @example
 * ```typescript
 * const { startDate, endDate } = getCurrentMonthRange();
 * console.log(startDate); // 2024-01-01T00:00:00.000Z
 * console.log(endDate);   // 2024-01-31T23:59:59.999Z
 * ```
 */
export function getCurrentMonthRange(): { startDate: Date; endDate: Date } {
  const now = new Date()
  const startDate = new Date(now.getFullYear(), now.getMonth(), 1)
  const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)

  return { startDate, endDate }
}

/**
 * Converts a Date object to a date-only string in YYYY-MM-DD format using local time.
 * This avoids timezone conversion issues that occur with toISOString().
 *
 * @param date - The Date object to convert
 * @returns Date string in YYYY-MM-DD format based on local time
 * @throws Error if date is invalid
 * @example
 * ```typescript
 * const date = new Date(2025, 6, 1); // July 1, 2025 local time
 * const dateStr = toDateOnlyStringLocal(date);
 * console.log(dateStr); // "2025-07-01" regardless of timezone
 * ```
 */
export function toDateOnlyStringLocal(date: Date): string {
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date provided to toDateOnlyStringLocal')
  }

  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * Parses a date-only string (YYYY-MM-DD) to a Date object in local time.
 * This avoids timezone conversion issues that occur with Date constructor parsing.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object representing the date at 00:00:00 local time
 * @throws Error if date string is invalid or malformed
 * @example
 * ```typescript
 * const date = parseDateOnlyStringLocal("2025-07-01");
 * console.log(date); // July 1, 2025 00:00:00 local time
 * ```
 */
export function parseDateOnlyStringLocal(dateString: string): Date {
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) {
    throw new Error(`Invalid date string format: ${dateString}. Expected YYYY-MM-DD`)
  }

  const [, yearStr, monthStr, dayStr] = match
  if (!yearStr || !monthStr || !dayStr) {
    throw new Error(`Invalid date string format: ${dateString}. Expected YYYY-MM-DD`)
  }
  const year = parseInt(yearStr, 10)
  const month = parseInt(monthStr, 10) - 1 // Month is 0-indexed
  const day = parseInt(dayStr, 10)

  const date = new Date(year, month, day)

  // Validate that the parsed date matches the input (catches invalid dates like Feb 30)
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
    throw new Error(`Invalid date: ${dateString}`)
  }

  return date
}

/**
 * Checks if a date range spans less than one calendar month.
 * Accounts for varying month lengths (28-31 days).
 *
 * @param startDate - The start date of the range
 * @param endDate - The end date of the range
 * @returns True if the range is less than a full calendar month, false otherwise
 * @example
 * ```typescript
 * const start = new Date(2025, 0, 15); // Jan 15, 2025
 * const end = new Date(2025, 1, 10);   // Feb 10, 2025
 * console.log(isDateRangeLessThanMonth(start, end)); // false (spans > 1 month)
 *
 * const start2 = new Date(2025, 0, 15); // Jan 15, 2025
 * const end2 = new Date(2025, 1, 13);   // Feb 13, 2025
 * console.log(isDateRangeLessThanMonth(start2, end2)); // true (29 days)
 * ```
 */
export function isDateRangeLessThanMonth(startDate: Date | null, endDate: Date | null): boolean {
  if (!startDate || !endDate) return false

  // Calculate the date that is exactly one month from the start
  // This accounts for varying month lengths
  const oneMonthFromStart = new Date(
    startDate.getFullYear(),
    startDate.getMonth() + 1,
    startDate.getDate()
  )

  // If end date is before the one-month mark, the range is less than a month
  return endDate < oneMonthFromStart
}

/**
 * First and last day of month `idx` in `year`. If `idx` is the current month
 * (relative to `now`), the end is capped to `now` so future days aren't selected.
 *
 * @param year - The calendar year
 * @param idx - Zero-based month index (0 = January, 11 = December)
 * @param now - Reference "today" date (defaults to `new Date()`)
 * @returns Object with `start` (1st of month) and `end` (last day or `now` if current month)
 * @example
 * ```typescript
 * monthRange(2025, 2, new Date(2025, 5, 15)) // March 2025 → { start: Mar 1, end: Mar 31 }
 * monthRange(2025, 5, new Date(2025, 5, 9))  // June 2025 (current) → { start: Jun 1, end: Jun 9 }
 * ```
 */
export function monthRange(
  year: number,
  idx: number,
  now: Date = new Date()
): { start: Date; end: Date } {
  const start = new Date(year, idx, 1)
  const isCurrentMonth = year === now.getFullYear() && idx === now.getMonth()
  const end = isCurrentMonth ? now : new Date(year, idx + 1, 0)
  return { start, end }
}

/**
 * Whether month `idx` of `year` overlaps the active [start, end] selection.
 * Used to highlight grid month buttons from the current filter range.
 *
 * @param idx - Zero-based month index to test (0 = January, 11 = December)
 * @param start - Start of the active date selection, or `null` if unset
 * @param end - End of the active date selection, or `null` if unset
 * @param year - The calendar year the month belongs to
 * @param now - Reference "today" date (defaults to `new Date()`)
 * @returns `true` when the month interval overlaps [start, end]; `false` otherwise
 * @example
 * ```typescript
 * const start = new Date(2025, 2, 1) // Mar 1
 * const end   = new Date(2025, 4, 31) // May 31
 * isMonthInRange(2, start, end, 2025) // => true  (March)
 * isMonthInRange(5, start, end, 2025) // => false (June)
 * ```
 */
export function isMonthInRange(
  idx: number,
  start: Date | null,
  end: Date | null,
  year: number,
  now: Date = new Date()
): boolean {
  if (!start || !end) return false
  const { start: mStart, end: mEnd } = monthRange(year, idx, now)
  return mStart.getTime() <= end.getTime() && mEnd.getTime() >= start.getTime()
}

/**
 * Builds the date exactly `n` calendar months after `date`, preserving day-of-month
 * (with natural JS overflow, e.g. Jan 31 + 1 month → Mar 3).
 */
function addCalendarMonths(date: Date, n: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + n, date.getDate())
}

/**
 * Builds the date exactly `n` calendar years after `date`.
 */
function addCalendarYears(date: Date, n: number): Date {
  return new Date(date.getFullYear() + n, date.getMonth(), date.getDate())
}

/**
 * Infers the time aggregation for a date range, preserving the user's manual
 * Daily/Weekly choice for mid-size ranges.
 *
 * Rules:
 * - **< 1 week** → always `Daily`
 * - **≥ 2 months** → coarse ladder: `Monthly`, then `Quarterly` (≥ 1 year),
 *   then `Yearly` (≥ 3 years)
 * - **in between (1 week – 2 months)** → preserve the current aggregation when it
 *   is already `Daily`/`Weekly`; otherwise fall back to `Daily` (< 1 month) or
 *   `Weekly` (≥ 1 month). `Weekly` is therefore only ever reached via the user's
 *   manual selection or this fallback — never forced over an explicit choice.
 *
 * @param startDate - Range start
 * @param endDate - Range end
 * @param current - Currently selected aggregation (preserved within the flexible band)
 * @returns The aggregation to apply for the range
 * @example
 * ```typescript
 * inferTimeAggregation(jan1, jan3, 'Monthly')   // => 'Daily'  (< 1 week)
 * inferTimeAggregation(jan1, feb20, 'Weekly')   // => 'Weekly' (band, preserved)
 * inferTimeAggregation(jan1, jun1, 'Daily')     // => 'Monthly' (≥ 2 months)
 * ```
 */
export function inferTimeAggregation(
  startDate: Date,
  endDate: Date,
  current: AggregationType
): AggregationType {
  const inclusiveDays = differenceInCalendarDays(endDate, startDate) + 1

  // < 1 week → always the finest granularity
  if (inclusiveDays < 7) return 'Daily'

  // ≥ 2 months → coarse ladder (Monthly → Quarterly → Yearly)
  if (endDate >= addCalendarMonths(startDate, 2)) {
    if (endDate >= addCalendarYears(startDate, 3)) return 'Yearly'
    if (endDate >= addCalendarYears(startDate, 1)) return 'Quarterly'
    return 'Monthly'
  }

  // Flexible band [1 week, 2 months): keep an explicit fine-grained choice...
  if (current === 'Daily' || current === 'Weekly') return current

  // ...otherwise (coarse or initial state) pick a sensible default by sub-range.
  return endDate >= addCalendarMonths(startDate, 1) ? 'Weekly' : 'Daily'
}

/** Preset identifier the date-filter UI derives from the current range. */
export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'this-month'
  | 'previous-month'
  | 'this-year'
  | `month-${number}`
  | 'custom'

/**
 * Classifies a start/end date pair as one of the date-filter presets, or
 * 'custom' when it matches none. Single source of truth shared by the
 * DatePresetSelector caption and the page-level DateRangeHeading — the two
 * must never disagree about what range is active.
 *
 * @param startDate - Range start (time of day ignored)
 * @param endDate - Range end (time of day ignored)
 * @param now - Reference "today" (injectable for tests)
 * @returns The matching preset id
 * @example
 * ```typescript
 * classifyDateRange(new Date(2026, 5, 1), new Date(2026, 5, 10)) // => 'this-month' (on 10 Jun 2026)
 * classifyDateRange(new Date(2026, 4, 1), new Date(2026, 4, 11)) // => 'custom'
 * ```
 */
export function classifyDateRange(
  startDate: Date | null,
  endDate: Date | null,
  now: Date = new Date()
): DateRangePreset {
  const atStart = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const today = atStart(now)
  const startOnly = startDate ? atStart(startDate) : null
  const endOnly = endDate ? atStart(endDate) : null

  if (!startOnly || !endOnly) {
    return 'this-month'
  }

  if (startOnly.getTime() === today.getTime() && endOnly.getTime() === today.getTime()) {
    return 'today'
  }

  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (startOnly.getTime() === yesterday.getTime() && endOnly.getTime() === yesterday.getTime()) {
    return 'yesterday'
  }

  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1)
  if (startOnly.getTime() === thisMonthStart.getTime() && endOnly.getTime() === today.getTime()) {
    return 'this-month'
  }

  const prevStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const prevEnd = new Date(today.getFullYear(), today.getMonth(), 0)
  if (startOnly.getTime() === prevStart.getTime() && endOnly.getTime() === prevEnd.getTime()) {
    return 'previous-month'
  }

  const thisYearStart = new Date(today.getFullYear(), 0, 1)
  if (startOnly.getTime() === thisYearStart.getTime() && endOnly.getTime() === today.getTime()) {
    return 'this-year'
  }

  // Detect full-month selections within the current year
  for (let m = 0; m < 12; m++) {
    const mStart = new Date(today.getFullYear(), m, 1)
    const mEnd = new Date(today.getFullYear(), m + 1, 0)
    if (startOnly.getTime() === mStart.getTime() && endOnly.getTime() === mEnd.getTime()) {
      return `month-${m}`
    }
  }

  return 'custom'
}

/**
 * Formats a date range for display, collapsing shared parts: same day shows a
 * single date, same month collapses the start's month, different years spell
 * both out. Mirrors the en-dash style of {@link formatIsoWeekRange}.
 *
 * @param start - Range start
 * @param end - Range end
 * @param locale - Current UI locale ('en' or 'pl')
 * @returns Human-readable range
 * @example
 * ```typescript
 * formatDateRange(new Date(2026, 4, 1), new Date(2026, 4, 11)) // => "1 – 11 May"
 * formatDateRange(new Date(2026, 3, 28), new Date(2026, 4, 11)) // => "28 Apr – 11 May"
 * formatDateRange(new Date(2026, 4, 11), new Date(2026, 4, 11)) // => "11 May"
 * ```
 */
export function formatDateRange(start: Date, end: Date, locale: string = 'en'): string {
  const dateLocale = locale === 'pl' ? pl : enUS
  const sameYear = start.getFullYear() === end.getFullYear()
  const sameMonth = sameYear && start.getMonth() === end.getMonth()
  const sameDay = sameMonth && start.getDate() === end.getDate()

  if (!sameYear) {
    return `${format(start, 'd MMM yyyy', { locale: dateLocale })} – ${format(end, 'd MMM yyyy', { locale: dateLocale })}`
  }
  if (sameDay) {
    return format(start, 'd MMM', { locale: dateLocale })
  }
  const startPattern = sameMonth ? 'd' : 'd MMM'
  return `${format(start, startPattern, { locale: dateLocale })} – ${format(end, 'd MMM', { locale: dateLocale })}`
}
